import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, formatError } from "../api";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { resolveMediaUrl } from "../components/PageContent";

export default function Cart() {
  const { items, update, remove, total, count } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [shopifyConfigured, setShopifyConfigured] = useState(false);

  useEffect(() => {
    api.get("/shop/status").then((r) => setShopifyConfigured(!!r.data?.configured)).catch(() => {});
  }, []);

  const checkout = async () => {
    if (items.length === 0) {
      toast.error("Your bag is empty.");
      return;
    }
    setLoading(true);
    try {
      // If Shopify is wired up AND every line has a Shopify variant id, route to Shopify checkout.
      const allHaveVariants = items.every((i) => !!i.variant_id);
      if (shopifyConfigured && allHaveVariants) {
        const { data } = await api.post("/shop/cart", {
          lines: items.map((i) => ({ variantId: i.variant_id, quantity: i.quantity })),
        });
        if (!data?.checkout_url) throw new Error("Shopify did not return a checkout URL.");
        window.location.href = data.checkout_url;
        return;
      }
      // Fallback: legacy Stripe checkout for the seeded products
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: email || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e?.message || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="bg-bone min-h-screen" data-testid="cart-page">
      <SEO title="Your Bag" path="/cart" noindex />
      <section className="bg-asphalt text-bone py-12">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-2">✦ Goods in transit</div>
          <h1 className="font-display text-5xl uppercase tracking-tighter">Your Bag</h1>
        </div>
      </section>
      <div className="tape-divider" />
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {items.length === 0 ? (
            <div className="border-2 border-asphalt bg-card-light shadow-hard p-10 text-center">
              <div className="font-western text-3xl mb-3">Your bag is sad and empty.</div>
              <p className="font-body text-ink mb-6">Go put a deck in it.</p>
              <Link
                to="/shop"
                data-testid="cart-empty-shop-cta"
                className="inline-block bg-terra text-bone border-2 border-asphalt px-6 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#1C1B1A] transition-all"
              >
                Spend Some Money →
              </Link>
            </div>
          ) : (
            <div className="border-2 border-asphalt bg-card-light shadow-hard divide-y-2 divide-asphalt">
              {items.map((i, idx) => (
                <div key={i.line_key || i.product_id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${idx}`}>
                  <Link to={`/shop/${i.slug}`} className="block w-20 h-20 border-2 border-asphalt bg-bone shrink-0">
                    {i.image && <img src={resolveMediaUrl(i.image)} alt={i.name} className="w-full h-full object-cover ink-stamp" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl uppercase tracking-tight truncate">{i.name}</div>
                    {i.variant_title && (
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra" data-testid={`cart-variant-${idx}`}>
                        {i.variant_title}
                      </div>
                    )}
                    <div className="font-mono text-xs text-ink">${i.price.toFixed(2)} each</div>
                  </div>
                  <div className="flex items-center border-2 border-asphalt">
                    <button onClick={() => update(i.line_key || i.product_id, i.quantity - 1)} className="p-2 hover:bg-asphalt hover:text-bone" data-testid={`cart-decrement-${idx}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                    <div className="px-3 font-display">{i.quantity}</div>
                    <button onClick={() => update(i.line_key || i.product_id, i.quantity + 1)} className="p-2 hover:bg-asphalt hover:text-bone" data-testid={`cart-increment-${idx}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="font-display text-lg w-20 text-right">${(i.price * i.quantity).toFixed(2)}</div>
                  <button onClick={() => remove(i.line_key || i.product_id)} className="p-2 border-2 border-asphalt hover:bg-terra hover:text-bone" data-testid={`cart-remove-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-4">
          <div className="border-2 border-asphalt bg-asphalt text-bone shadow-hard p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60 mb-4">✦ Order Summary</div>
            <div className="flex justify-between font-body text-base mb-2">
              <span>Items</span>
              <span data-testid="cart-summary-count">{count}</span>
            </div>
            <div className="flex justify-between font-body text-base mb-2">
              <span>Shipping</span>
              <span className="font-mono text-xs">calculated by the postal mule</span>
            </div>
            <div className="border-t-2 border-bone/30 my-4" />
            <div className="flex justify-between font-display text-3xl mb-6">
              <span>Total</span>
              <span data-testid="cart-summary-total">${total.toFixed(2)}</span>
            </div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-2">
              Email {shopifyConfigured ? "(collected at checkout)" : "(optional)"}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@somewhere.tx"
              data-testid="cart-email-input"
              disabled={shopifyConfigured}
              className="w-full bg-bone text-asphalt px-3 py-2 border-2 border-bone font-mono text-sm mb-4 disabled:opacity-50"
            />
            <button
              onClick={checkout}
              disabled={items.length === 0 || loading}
              data-testid="checkout-btn"
              className="w-full bg-terra text-bone border-2 border-bone px-6 py-4 font-display tracking-wider uppercase text-xl shadow-hard-bone hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#F5F2E9] transition-all disabled:opacity-50"
            >
              {loading ? "Saddling up…" : "Proceed to Checkout"}
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/60 mt-3 text-center">
              {shopifyConfigured ? "Checkout handled by Shopify. Fulfilled by BoardPusher." : "Secured by Stripe. Refused by no one."}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
